import { Dispatch } from '@reduxjs/toolkit';

import { ActionConsts } from '@Definitions/index';
import { IDeliveryEstimate } from '@Reducers/order';
import { OrderService } from '@Services/index';
import { CONSULTATION_TYPE, ItemType } from '@Utils/constants';
import { formatOrderData, sortCounsellingOrders } from '@Utils/Helpers/Order/helper';
import { formatResources } from '@Utils/Helpers/resource.helper';
import { LOG_ERROR } from '@Utils/logger';

export const OrderActions = {
  UpdateOrderConfirmed: (orderConfirmed: boolean) => async (dispatch: Dispatch) => {
    dispatch({
      orderConfirmed,
      type: ActionConsts.Order.UpdateOrderConfirmed,
    });
  },
  // eslint-disable-next-line unicorn/consistent-function-scoping
  GetConsultationOrders: (requestPayload: { patientId?: string }) => async (dispatch: Dispatch) => {
    try {
      dispatch({ type: ActionConsts.Order.GetConsultationOrdersInit });
      const result = await OrderService.GetConsultationOrders(requestPayload?.patientId, 50);
      if (result?.status === 200 && result?.data?.data?.length === 0) {
        dispatch({
          consultationOrders: result?.data?.data,
          type: ActionConsts.Order.GetConsultationOrdersSuccess,
        });
      } else if (result?.status === 200 && result?.data?.data?.length > 0) {
        let counsellingOrders = result?.data?.data?.filter(
          (consultation) =>
            consultation?.consultationType?.code === CONSULTATION_TYPE.TH && consultation?.status !== 'cancelled',
        );
        let followupOrders = result?.data?.data?.filter(
          (consultation) => consultation?.consultationType?.code === CONSULTATION_TYPE.FU,
        );
        let patientQueryOrders = result?.data?.data?.filter(
          (consultation) => consultation?.consultationType?.code === CONSULTATION_TYPE.PQ,
        );
        let reportReadingOrders = result?.data?.data?.filter(
          (consultation) => consultation?.consultationType?.code === CONSULTATION_TYPE.RR,
        );
        let screeningCallOrders = result?.data?.data?.filter(
          (consultation) =>
            consultation?.consultationType?.code === CONSULTATION_TYPE.SC ||
            consultation?.consultationType?.code === CONSULTATION_TYPE.FC,
        );
        counsellingOrders = sortCounsellingOrders(counsellingOrders);
        patientQueryOrders = sortCounsellingOrders(patientQueryOrders);
        reportReadingOrders = sortCounsellingOrders(reportReadingOrders);
        screeningCallOrders = sortCounsellingOrders(screeningCallOrders);
        followupOrders = sortCounsellingOrders(followupOrders);

        dispatch({
          consultationOrders: {
            counsellingOrders: formatOrderData(counsellingOrders, new Date().toISOString()).reverse(),
            followupOrders: formatOrderData(followupOrders, new Date().toISOString()).reverse(),
            patientQueryOrders: formatOrderData(patientQueryOrders, new Date().toISOString()).reverse(),
            reportReadingOrders: formatOrderData(reportReadingOrders, new Date().toISOString()).reverse(),
            screeningCallOrders: formatOrderData(screeningCallOrders, new Date().toISOString()).reverse(),
          },
          type: ActionConsts.Order.GetConsultationOrdersSuccess,
        });
      } else {
        dispatch({ type: ActionConsts.Order.GetConsultationOrdersFail });
      }
    } catch (error) {
      LOG_ERROR('Error: OrderActions>>GetConsultationOrders', error);
      dispatch({ type: ActionConsts.Order.GetConsultationOrdersFail });
    }
  },
  GetPendingOrders: (payload: { showArticles?: boolean }) => async (dispatch: Dispatch) => {
    try {
      dispatch({ type: ActionConsts.Order.GetPendingOrdersInit });
      const result = await OrderService.GetPendingOrders({
        showArticles: payload?.showArticles,
      });
      if (result?.status === 200) {
        const orders = result?.data?.orders;
        const resources = result?.data?.articles;
        const drugs = orders?.filter((order) => order?.drugId);
        const labTests = orders?.filter((order) => order?.labTestId);
        const consultations = orders?.filter((order) => order?.consultationId);
        const extractPendingOrders = (orders) => {
          const encounterIds = [...new Set(orders?.map((order) => order?.encounterId))];
          const lastEncounterOrders = orders?.filter((order) => order?.encounterId === encounterIds?.[0]);
          return {
            count: orders?.length,
            encounterIds,
            lastEncounterId: encounterIds?.[0],
            lastEncounterOrders,
          };
        };
        dispatch({
          pendingOrders: {
            [ItemType.DRUG]: extractPendingOrders(drugs),
            [ItemType.LAB]: extractPendingOrders(labTests),
            [ItemType.CONSULTATION]: extractPendingOrders(consultations),
          },
          type: ActionConsts.Order.GetPendingOrdersSuccess,
        });
        dispatch({
          resources: formatResources(resources),
          type: ActionConsts.Resource.GetResourcesSuccess,
        });
      } else {
        dispatch({ type: ActionConsts.Order.GetPendingOrdersFail });
      }
    } catch (error) {
      LOG_ERROR('Error: OrderActions>>GetPendingOrders', error);
      dispatch({ type: ActionConsts.Order.GetPendingOrdersFail });
    }
  },
  // eslint-disable-next-line unicorn/consistent-function-scoping
  ResetOrderData: () => async (dispatch: Dispatch) => {
    dispatch({
      type: ActionConsts.Order.ResetOrderData,
    });
  },
  // eslint-disable-next-line unicorn/consistent-function-scoping
  ResetConsultationData: () => async (dispatch: Dispatch) => {
    dispatch({
      type: ActionConsts.Order.ResetConsultationData,
    });
  },
  // eslint-disable-next-line unicorn/consistent-function-scoping
  ResetInvoiceData: () => async (dispatch: Dispatch) => {
    dispatch({
      type: ActionConsts.Order.ResetPatientInvoices,
    });
  },
  // eslint-disable-next-line unicorn/consistent-function-scoping
  ResetConsultationOrders: () => async (dispatch: Dispatch) => {
    dispatch({
      type: ActionConsts.Order.ResetConsultationOrders,
    });
  },
  GetDeliveryEstimates: (payload: { delivery_postcode: number; weight: number }) => async (dispatch: Dispatch) => {
    try {
      dispatch({ type: ActionConsts.Order.GetDeliveryEstimatesInit });
      const result = await OrderService.GetDeliveryEstimates(payload);
      if (result?.status === 200) {
        const blueDartSurface = result?.data?.find((estimate: IDeliveryEstimate) =>
          estimate?.courier_name.toLowerCase().trim().includes('blue dart surface'),
        );
        const fastestEstimate = result?.data
          ?.filter((estimate: IDeliveryEstimate) => !estimate?.courier_name.toLowerCase().trim().includes('air'))
          ?.sort(
            (a: IDeliveryEstimate, b: IDeliveryEstimate) => new Date(a.etd).getTime() - new Date(b.etd).getTime(),
          )?.[0];
        dispatch({
          type: ActionConsts.Order.GetDeliveryEstimatesSuccess,
          deliveryEstimate: blueDartSurface || fastestEstimate,
        });
      } else {
        dispatch({ type: ActionConsts.Order.GetDeliveryEstimatesFail });
      }
    } catch (error) {
      LOG_ERROR('Error: OrderActions>>GetDeliveryEstimates', error);
      dispatch({ type: ActionConsts.Order.GetDeliveryEstimatesFail });
    }
  },
};
